import { Builder, withChildren } from '@builder.io/react'
import { useState, useEffect } from 'react'
import { useKeenSlider } from 'keen-slider/react'
import { LiaAngleLeftSolid, LiaAngleRightSolid } from 'react-icons/lia'
import React from 'react'

// Autoplay plugin for keen-slider
function AutoplayPlugin(slider, slideDelay = 4500) {
  let timeout;
  let mouseOver = false;

  function clearNextTimeout() {
    clearTimeout(timeout);
  }

  function nextTimeout() {
    clearTimeout(timeout);
    if (mouseOver) return;
    timeout = setTimeout(() => {
      slider.next();
    }, slideDelay); // Configurable seconds per slide
  }

  slider.on("created", () => {
    slider.container.addEventListener("mouseover", () => {
      mouseOver = true;
      clearNextTimeout();
    });
    slider.container.addEventListener("mouseout", () => {
      mouseOver = false;
      nextTimeout();
    });
    nextTimeout();
  });
  slider.on("dragStarted", clearNextTimeout);
  slider.on("animationEnded", nextTimeout);
  slider.on("updated", nextTimeout);
}

const CustomBuilderWrapperCarousel = (props) => {
  const { 
    children, 
    wrapperClasses = '', 
    isCarousel = false,
    isDesktopCarousel = false,
    isMobileCarousel = false,
    loop = true,
    spacing = 20,
    perView = { desktop: 3, tablet: 2, mobile: 1 },
    showNavigation = true,
    slideDelay = 4500
  } = props

  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [pause, setPause] = useState(false)

  // Responsive perView
  const getPerView = () => {
    if (window.innerWidth < 640) return perView.mobile || 1;
    if (window.innerWidth < 1024) return perView.tablet || 2;
    return perView.desktop || 3;
  };
  const [perViewValue, setPerViewValue] = useState(getPerView());

  // Update perView on resize
  useEffect(() => {
    const handleResize = () => setPerViewValue(getPerView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only use slider if there are more children than perViewValue
  const useSlider = React.Children.count(children) > perViewValue;

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      mode: 'snap',
      loop,
      breakpoints: {
        '(min-width: 320px)': {
          slides: { perView: perView.mobile, spacing: spacing },
        },
        '(min-width: 768px)': {
          slides: { perView: perView.tablet, spacing: spacing },
        },
        '(min-width: 992px)': {
          slides: { perView: perView.desktop, spacing: spacing },
        },
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
      slides: {
        origin: 'auto',
      },
    },
    useSlider ? [(slider) => AutoplayPlugin(slider, slideDelay)] : []
  );
  
  // Determine if carousel should be active
  const shouldShowCarousel = isCarousel || 
    (isDesktopCarousel && window.innerWidth >= 768) || 
    (isMobileCarousel && window.innerWidth < 768)

  return (
    <div className={wrapperClasses}>
      <div className="w-full relative">
        {/* Navigation arrows - exactly like appealWidgetNewWithCarousel.jsx */}
        <div className={`${shouldShowCarousel && useSlider ? 'flex' : 'flex md:hidden'}`}>
          {showNavigation && shouldShowCarousel && useSlider && instanceRef.current && (
            <>
              <div
                className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[20%] -left-[0px] md:-left-[16px] xl:-left-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                onClick={e => e.stopPropagation() || instanceRef.current?.prev()}
              >
                <LiaAngleLeftSolid size={25} />
              </div>
              <div
                className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[20%] -right-[0px] md:-right-[16px] xl:-right-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                onClick={e => e.stopPropagation() || instanceRef.current?.next()}
              >
                <LiaAngleRightSolid size={25} />
              </div>
            </>
          )}
        </div>

        {shouldShowCarousel && useSlider ? (
          <div
            ref={sliderRef}
            className="keen-slider"
            onMouseEnter={() => setPause(true)}
            onMouseLeave={() => setPause(false)}
          >
            {React.Children.map(children, (child, index) => (
              <div
                key={`carousel_${index}`}
                className="keen-slider__slide basis-full flex justify-center w-full flex-1"
              >
                {child}
              </div>
            ))}
          </div>
        ) : (
          <div className="hidden md:flex flex-wrap gap-3 sm:gap-6 justify-center items-start w-full">
            {React.Children.map(children, (child, index) => (
              <div
                key={`static_${index}`}
                className="w-full flex-1"
              >
                {child}
              </div>
            ))}
          </div>
        )}

        {/* Mobile carousel when not enough items for desktop grid */}
        {!shouldShowCarousel && (
          <div
            ref={sliderRef}
            className="keen-slider md:!hidden"
            onMouseEnter={() => setPause(true)}
            onMouseLeave={() => setPause(false)}
          >
            {React.Children.map(children, (child, index) => (
              <div
                key={`mobile_carousel_${index}`}
                className="keen-slider__slide basis-full flex justify-center w-full flex-1"
              >
                {child}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const WrapperWithChildren = withChildren(CustomBuilderWrapperCarousel)

Builder.registerComponent(WrapperWithChildren, {
  name: 'CustomBuilderWrapperCarousel',
  canHaveChildren: true,
  inputs: [
    {
      name: 'wrapperClasses',
      type: 'string',
      friendlyName: 'Tailwind Classes for Wrapper',
    },
    {
      name: 'isCarousel',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'Enable Carousel (All Devices)',
      helperText: 'Enable carousel functionality for all screen sizes.',
    },
    {
      name: 'isDesktopCarousel',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'Enable Desktop Carousel',
      helperText: 'Enable carousel functionality only on desktop devices (768px+).',
    },
    {
      name: 'isMobileCarousel',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'Enable Mobile Carousel',
      helperText: 'Enable carousel functionality only on mobile devices (below 768px).',
    },
    {
      name: 'loop',
      type: 'boolean',
      defaultValue: true,
      friendlyName: 'Loop Carousel',
      helperText: 'Enable infinite loop for carousel.',
    },
    {
      name: 'spacing',
      type: 'number',
      defaultValue: 20,
      friendlyName: 'Slide Spacing',
      helperText: 'Space between slides in pixels.',
    },
    {
      name: 'perView',
      type: 'object',
      friendlyName: 'Slides Per View',
      helperText: 'Number of slides visible at once for different screen sizes.',
      subFields: [
        {
          name: 'mobile',
          type: 'number',
          defaultValue: 1,
          friendlyName: 'Mobile (640px-)',
        },
        {
          name: 'tablet',
          type: 'number',
          defaultValue: 2,
          friendlyName: 'Tablet (640px-1024px)',
        },
        {
          name: 'desktop',
          type: 'number',
          defaultValue: 3,
          friendlyName: 'Desktop (1024px+)',
        },
      ],
      defaultValue: { desktop: 3, tablet: 2, mobile: 1 },
    },
    {
      name: 'showNavigation',
      type: 'boolean',
      defaultValue: true,
      friendlyName: 'Show Navigation Arrows',
      helperText: 'Show left/right navigation arrows for the carousel.',
    },
    {
      name: 'slideDelay',
      type: 'number',
      defaultValue: 4500,
      friendlyName: 'Slide Delay (milliseconds)',
      helperText: 'Time in milliseconds before moving to the next slide (1000ms = 1 second).',
    },
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'Custom Builder Wrapper Carousel',
        },
      },
    },
  ],
})

export default CustomBuilderWrapperCarousel
